import React, { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import { 
  Settings, Search, Plus, Minus, GripVertical, Eye, EyeOff,
  ArrowUp, ArrowDown, X, Save, RotateCcw
} from 'lucide-react';
import { 
  PRODUCT_COLUMN_CATALOG, 
  SECTIONS, 
  getColumnsBySection,
  filterColumnsByRBAC,
  DEFAULT_VARIANTS
} from '../product/columns/catalog';

const ViewSettingsModal = ({ 
  open, 
  onClose, 
  currentVariant, 
  columnSizing = {}, // Column width information for persistence
  onApplyVariant, 
  onSaveVariant,
  userRoles = ['finance'], // Phase 3: Updated to match new RBAC system
  onRoleChange = () => {}, // CRITICAL FIX: Role switcher callback
  onGetProductCount = () => 0 // CRITICAL FIX: Get current product count
}) => {
  // Add CSS styles for the modal with dark mode support
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      /* Modal backdrop */
      .zb-modal-backdrop {
        position: fixed;
        inset: 0;
        background: rgba(0,0,0,0.5);
        backdrop-filter: blur(2px);
        z-index: 1000;
      }

      /* Modal sheet - Dark mode support */
      .zb-modal {
        position: fixed;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: min(1100px, 92vw);
        max-height: min(80vh, 900px);
        overflow: hidden;
        border-radius: 12px;
        box-shadow: 0 10px 40px rgba(0,0,0,0.25);
        background: #ffffff;
        border: 1px solid #e5e7eb;
        z-index: 1001;
      }

      /* Dark mode styles */
      .dark .zb-modal {
        background: #111827;
        border-color: #374151;
        box-shadow: 0 10px 40px rgba(0,0,0,0.6);
      }

      /* Scrollable body */
      .zb-modal__body {
        overflow: auto;
        max-height: calc(80vh - 64px);
        padding: 16px 20px;
        background: #ffffff;
      }

      .dark .zb-modal__body {
        background: #111827;
      }
    `;
    document.head.appendChild(style);

    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);
  // State Management
  const [activeTab, setActiveTab] = useState('columns');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [columnOrder, setColumnOrder] = useState([]);
  const [sortRules, setSortRules] = useState([]);
  const [filters, setFilters] = useState([]);
  const [variantName, setVariantName] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [catalogError, setCatalogError] = useState(null);

  // ESC key handling and focus management
  useEffect(() => {
    if (!open) return;

    const onKey = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  // Initialize state from current variant with proper error handling and concurrency
  useEffect(() => {
    if (open) {
      setIsLoading(true);
      setCatalogError(null);
      
      // Clear search when opening modal (debounced reset)
      setSearchTerm('');
      
      try {
        // Simulate async loading for catalog and variant resolution
        setTimeout(() => {
          if (currentVariant) {
            console.log('ViewSettings: Initializing with variant:', currentVariant.name);
            setSelectedColumns(currentVariant.columns || []);
            setColumnOrder(currentVariant.columns || []);
            setSortRules(currentVariant.sort || []);
            setFilters(currentVariant.filters || []);
            setVariantName(currentVariant.name || '');
          } else {
            // Initialize with default values if no variant
            console.log('ViewSettings: No variant, using defaults');
            const defaultColumns = PRODUCT_COLUMN_CATALOG
              .filter(col => !col.hiddenByDefault)
              .map(col => col.id);
            setSelectedColumns(defaultColumns);
            setColumnOrder(defaultColumns);
            setSortRules([]);
            setFilters([]);
            setVariantName('');
          }
          setIsLoading(false);
        }, 150); // 150ms debounce for loading state
      } catch (error) {
        console.error('ViewSettings: Error loading variant:', error);
        setCatalogError('Failed to load variant configuration');
        setIsLoading(false);
      }
    }
  }, [currentVariant, open]);

  // Filter available columns by RBAC and search - Enhanced with debug logging and normalization
  const availableColumns = useMemo(() => {
    console.log('ViewSettings: Catalog entries:', PRODUCT_COLUMN_CATALOG.length);
    console.log('ViewSettings: User roles:', userRoles);
    
    if (!userRoles || userRoles.length === 0) {
      console.warn('ViewSettings: No user roles provided, using General fallback');
      const generalColumns = PRODUCT_COLUMN_CATALOG.filter(col => 
        col.section === 'General' || col.id.startsWith('general.')
      ).slice(0, 5);
      return generalColumns;
    }
    
    const filtered = filterColumnsByRBAC(PRODUCT_COLUMN_CATALOG, userRoles);
    console.log('ViewSettings: Available columns after RBAC:', filtered.length);
    
    // Ensure minimum columns for any role (General fields always visible)
    const generalColumns = PRODUCT_COLUMN_CATALOG.filter(col => 
      col.section === 'General' || col.id.startsWith('general.')
    );
    
    if (filtered.length === 0) {
      console.warn('ViewSettings: RBAC filtering returned 0 columns, using General fallback');
      return generalColumns.slice(0, 5); // Ensure at least 5 General fields
    }
    
    // Ensure General fields are always included
    const generalIds = generalColumns.map(col => col.id);
    const filteredIds = filtered.map(col => col.id);
    const missingGeneral = generalColumns.filter(col => !filteredIds.includes(col.id));
    
    const finalColumns = [...filtered, ...missingGeneral];
    console.log('ViewSettings: Final available columns:', finalColumns.length);
    
    return finalColumns;
  }, [userRoles]);

  // Debounced search with 150ms delay
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 150);
    
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const filteredColumns = useMemo(() => {
    if (!debouncedSearchTerm) return availableColumns;
    return availableColumns.filter(col => 
      col.label.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
      col.section.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
      col.id.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
    );
  }, [availableColumns, debouncedSearchTerm]);

  // ID normalization helper for safe key matching
  const normalizeId = (id) => {
    return id?.toLowerCase().replace(/[^a-z0-9]/g, '') || '';
  };

  // Group columns by section with normalized keys
  const columnsBySection = useMemo(() => {
    const sections = {};
    filteredColumns.forEach(col => {
      if (!sections[col.section]) sections[col.section] = [];
      sections[col.section].push(col);
    });
    return sections;
  }, [filteredColumns]);

  // Handle column selection
  const toggleColumn = (columnId) => {
    const newSelection = selectedColumns.includes(columnId)
      ? selectedColumns.filter(id => id !== columnId)
      : [...selectedColumns, columnId];
    
    setSelectedColumns(newSelection);
    
    // Update order for newly selected columns
    if (!selectedColumns.includes(columnId)) {
      setColumnOrder([...columnOrder, columnId]);
    } else {
      setColumnOrder(columnOrder.filter(id => id !== columnId));
    }
  };

  // Select/deselect all in section
  const toggleSection = (section) => {
    const sectionColumns = getColumnsBySection(section)
      .filter(col => availableColumns.includes(col))
      .map(col => col.id);
    
    const allSelected = sectionColumns.every(id => selectedColumns.includes(id));
    
    if (allSelected) {
      // Deselect all in section
      const newSelection = selectedColumns.filter(id => !sectionColumns.includes(id));
      setSelectedColumns(newSelection);
      setColumnOrder(columnOrder.filter(id => !sectionColumns.includes(id)));
    } else {
      // Select all in section  
      const newColumns = sectionColumns.filter(id => !selectedColumns.includes(id));
      setSelectedColumns([...selectedColumns, ...newColumns]);
      setColumnOrder([...columnOrder, ...newColumns]);
    }
  };

  // Reorder columns
  const moveColumn = (fromIndex, toIndex) => {
    const newOrder = [...columnOrder];
    const [moved] = newOrder.splice(fromIndex, 1);
    newOrder.splice(toIndex, 0, moved);
    setColumnOrder(newOrder);
  };

  // Sort rule management
  const addSortRule = () => {
    if (selectedColumns.length > 0) {
      setSortRules([...sortRules, { 
        id: selectedColumns[0], 
        desc: false 
      }]);
    }
  };

  const removeSortRule = (index) => {
    setSortRules(sortRules.filter((_, i) => i !== index));
  };

  const updateSortRule = (index, field, value) => {
    const newRules = [...sortRules];
    newRules[index] = { ...newRules[index], [field]: value };
    setSortRules(newRules);
  };

  // Apply current settings with error guardrails
  const handleApply = () => {
    try {
      // Validate that we have columns to apply
      const finalColumns = columnOrder.filter(id => selectedColumns.includes(id));
      
      if (finalColumns.length === 0) {
        console.warn('ViewSettings: No columns selected, falling back to defaults');
        const defaultColumns = availableColumns.slice(0, 5).map(col => col.id);
        
        const variant = {
          name: variantName || 'Custom View',
          columns: defaultColumns,
          sort: sortRules,
          filters: filters
        };
        onApplyVariant(variant);
      } else {
        const variant = {
          name: variantName || 'Custom View',
          columns: finalColumns,
          sort: sortRules,
          filters: filters,
          columnSizing: columnSizing // Include current column widths
        };
        onApplyVariant(variant);
      }
      onClose();
    } catch (error) {
      console.error('ViewSettings: Error applying variant, falling back to catalog defaults:', error);
      
      // Fallback to safe defaults
      const defaultColumns = PRODUCT_COLUMN_CATALOG
        .filter(col => !col.hiddenByDefault)
        .slice(0, 5)
        .map(col => col.id);
        
      const fallbackVariant = {
        name: 'Safe Default',
        columns: defaultColumns,
        sort: [],
        filters: []
      };
      onApplyVariant(fallbackVariant);
      onClose();
    }
  };

  // Save as new variant
  const handleSaveVariant = () => {
    const variant = {
      name: variantName || `My View ${Date.now()}`,
      columns: columnOrder.filter(id => selectedColumns.includes(id)),
      sort: sortRules,
      filters: filters,
      columnSizing: columnSizing, // Include current column widths for persistence
      isUserCreated: true
    };
    onSaveVariant(variant);
    onClose();
  };

  // Reset to defaults
  const handleReset = () => {
    const defaultVariant = DEFAULT_VARIANTS.OWNER_OPS;
    setSelectedColumns(defaultVariant.columns);
    setColumnOrder(defaultVariant.columns);
    setSortRules(defaultVariant.sort);
    setFilters(defaultVariant.filters);
    setVariantName('');
  };

  // Backdrop click handler
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  if (!open) return null;

  return createPortal(
    <div 
      className="zb-modal-backdrop" 
      role="dialog" 
      aria-modal="true" 
      onClick={handleBackdropClick}
    >
      <div className="zb-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900">
          <div>
            <h2 className="text-lg font-semibold flex items-center gap-2 text-gray-900 dark:text-white">
              <Settings className="w-5 h-5 text-gray-700 dark:text-gray-300" />
              View Settings — Product Master
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
              Customize columns, sorting, and filters for your Product Master view
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-md transition-colors text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            aria-label="Close modal"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="zb-modal__body">

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-4 bg-gray-100 dark:bg-gray-800 border-gray-200 dark:border-gray-700">
            <TabsTrigger value="columns" className="text-gray-600 dark:text-gray-400 data-[state=active]:text-gray-900 data-[state=active]:dark:text-white data-[state=active]:bg-white data-[state=active]:dark:bg-gray-700">Columns</TabsTrigger>
            <TabsTrigger value="sort" className="text-gray-600 dark:text-gray-400 data-[state=active]:text-gray-900 data-[state=active]:dark:text-white data-[state=active]:bg-white data-[state=active]:dark:bg-gray-700">Sort</TabsTrigger>
            <TabsTrigger value="filter" className="text-gray-600 dark:text-gray-400 data-[state=active]:text-gray-900 data-[state=active]:dark:text-white data-[state=active]:bg-white data-[state=active]:dark:bg-gray-700">Filter</TabsTrigger>
            <TabsTrigger value="settings" className="text-gray-600 dark:text-gray-400 data-[state=active]:text-gray-900 data-[state=active]:dark:text-white data-[state=active]:bg-white data-[state=active]:dark:bg-gray-700">Settings</TabsTrigger>
          </TabsList>

          {/* COLUMNS TAB */}
          <TabsContent value="columns" className="space-y-4">
            <div className="flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
                <Input
                  placeholder="Search columns..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 text-gray-900 dark:text-white bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-500"
                />
              </div>
              <Badge variant="secondary" className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600">
                {selectedColumns.length} selected
              </Badge>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Available Columns */}
              <div className="space-y-4">
                <h3 className="font-medium text-gray-900 dark:text-white">Available Columns</h3>
                
                {/* Stats Bar */}
                <div className="text-xs text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-2 rounded">
                  Total: {PRODUCT_COLUMN_CATALOG.length} | Available: {availableColumns.length} | Filtered: {filteredColumns.length}
                </div>
                
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 max-h-96 overflow-y-auto space-y-4 bg-white dark:bg-gray-900">
                  {isLoading ? (
                    // Skeleton loader while catalog and variant resolve
                    <div className="space-y-4">
                      {[1,2,3].map(i => (
                        <div key={i} className="space-y-2">
                          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-24 animate-pulse"></div>
                          {[1,2,3].map(j => (
                            <div key={j} className="flex items-center space-x-2 pl-4">
                              <div className="w-4 h-4 bg-gray-200 dark:bg-gray-700 rounded animate-pulse"></div>
                              <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded flex-1 animate-pulse"></div>
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  ) : catalogError ? (
                    // Error state with retry
                    <div className="text-center py-8">
                      <div className="text-red-500 dark:text-red-400 mb-2">⚠️ {catalogError}</div>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        onClick={() => window.location.reload()}
                        className="mt-2 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
                      >
                        Retry
                      </Button>
                    </div>
                  ) : Object.entries(columnsBySection).length === 0 ? (
                    // Empty state with contextual messages
                    <div className="text-center py-8">
                      <div className="text-gray-400 dark:text-gray-500 mb-2">
                        {availableColumns.length === 0 
                          ? 'No columns available for your role' 
                          : searchTerm 
                          ? 'No results found' 
                          : 'No columns available'
                        }
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-600">
                        {availableColumns.length === 0 
                          ? `RBAC filtering with roles [${userRoles.join(', ')}] returned no columns` 
                          : searchTerm 
                          ? `Search term "${searchTerm}" doesn't match any columns`
                          : 'Column catalog is empty'
                        }
                      </div>
                      {searchTerm && (
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={() => setSearchTerm('')}
                          className="mt-2 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600"
                        >
                          Clear Search
                        </Button>
                      )}
                    </div>
                  ) : (
                    Object.entries(columnsBySection).map(([section, columns]) => (
                    <div key={section} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">{section}</Label>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleSection(section)}
                          className="h-6 px-2 text-xs text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 hover:text-gray-900 dark:hover:text-gray-200"
                        >
                          {columns.every(col => selectedColumns.includes(col.id)) ? (
                            <><EyeOff className="w-3 h-3 mr-1" />Hide All</>
                          ) : (
                            <><Eye className="w-3 h-3 mr-1" />Select All</>
                          )}
                        </Button>
                      </div>
                      {columns.map(col => (
                        <div key={col.id} className="flex items-center space-x-2 pl-4 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg p-2 cursor-pointer">
                          <Checkbox
                            checked={selectedColumns.includes(col.id)}
                            onCheckedChange={() => toggleColumn(col.id)}
                            className="border-gray-300 dark:border-gray-600"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-gray-900 dark:text-white">{col.label}</div>
                            <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{col.id}</div>
                          </div>
                          <Badge variant="outline" className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600">
                            {col.type}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  ))
                  )}
                </div>
              </div>

              {/* Selected Columns Order */}
              <div className="space-y-4">
                <h3 className="font-medium text-gray-900 dark:text-white">Column Order & Width</h3>
                <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 max-h-96 overflow-y-auto space-y-2 bg-white dark:bg-gray-900">
                  {isLoading ? (
                    // Skeleton loader for column order
                    <div className="space-y-2">
                      {[1,2,3,4].map(i => (
                        <div key={i} className="flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-800 rounded animate-pulse">
                          <div className="w-4 h-4 bg-gray-200 dark:bg-gray-700 rounded"></div>
                          <div className="flex-1 space-y-1">
                            <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-3/4"></div>
                            <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded w-1/2"></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : columnOrder.filter(id => selectedColumns.includes(id)).length === 0 ? (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      <Eye className="w-8 h-8 mx-auto mb-2 text-gray-300 dark:text-gray-600" />
                      <p>Select columns to customize order</p>
                      <p className="text-xs mt-1">
                        {availableColumns.length} columns available in left pane
                      </p>
                    </div>
                  ) : (
                    columnOrder.filter(id => selectedColumns.includes(id)).map((colId, index) => {
                      const col = availableColumns.find(c => c.id === colId);
                      if (!col) return null;
                    
                    return (
                      <div key={colId} className="flex items-center gap-2 p-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded hover:border-blue-500 dark:hover:border-blue-400 cursor-move">
                        <GripVertical className="w-4 h-4 text-gray-400 dark:text-gray-500 cursor-move" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">{col.label}</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">{col.section}</div>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => index > 0 && moveColumn(index, index - 1)}
                            disabled={index === 0}
                            className="h-6 w-6 p-0"
                          >
                            <ArrowUp className="w-3 h-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => index < columnOrder.length - 1 && moveColumn(index, index + 1)}
                            disabled={index === columnOrder.length - 1}
                            className="h-6 w-6 p-0"
                          >
                            <ArrowDown className="w-3 h-3" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleColumn(colId)}
                            className="h-6 w-6 p-0 text-red-600 hover:text-red-700"
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    );
                    })
                  )}
                </div>
              </div>
            </div>
          </TabsContent>

          {/* SORT TAB */}
          <TabsContent value="sort" className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="font-medium">Sort Rules</h3>
              <Button onClick={addSortRule} size="sm" className="gap-2">
                <Plus className="w-4 h-4" />Add Rule
              </Button>
            </div>
            
            <div className="space-y-3">
              {sortRules.map((rule, index) => {
                const col = availableColumns.find(c => c.id === rule.id);
                return (
                  <div key={index} className="flex items-center gap-3 p-3 border rounded-lg">
                    <span className="text-sm font-medium w-8">{index + 1}.</span>
                    <select
                      value={rule.id}
                      onChange={(e) => updateSortRule(index, 'id', e.target.value)}
                      className="flex-1 p-2 border rounded text-sm"
                    >
                      {selectedColumns.map(colId => {
                        const column = availableColumns.find(c => c.id === colId);
                        return (
                          <option key={colId} value={colId}>
                            {column?.label} ({column?.section})
                          </option>
                        );
                      })}
                    </select>
                    <select
                      value={rule.desc ? 'desc' : 'asc'}
                      onChange={(e) => updateSortRule(index, 'desc', e.target.value === 'desc')}
                      className="w-24 p-2 border rounded text-sm"
                    >
                      <option value="asc">A→Z</option>
                      <option value="desc">Z→A</option>
                    </select>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeSortRule(index)}
                      className="text-red-600 hover:text-red-700"
                    >
                      <Minus className="w-4 h-4" />
                    </Button>
                  </div>
                );
              })}
              
              {sortRules.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <ArrowUp className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                  <p>No sorting rules defined</p>
                  <p className="text-xs">Add rules to sort your data</p>
                </div>
              )}
            </div>
          </TabsContent>

          {/* FILTER TAB */}
          <TabsContent value="filter" className="space-y-4">
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <Search className="w-8 h-8 mx-auto mb-2 text-gray-300 dark:text-gray-600" />
              <p>Advanced filtering coming soon</p>
              <p className="text-xs">Category, status, and numeric range filters</p>
            </div>
          </TabsContent>

          {/* SETTINGS TAB */}
          <TabsContent value="settings" className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label className="text-sm font-medium text-gray-700 dark:text-gray-300">View Role</Label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                  Switch between different roles to see relevant columns and permissions
                </p>
                <select 
                  value={userRoles[0]} 
                  onChange={(e) => onRoleChange(e.target.value)}
                  className="w-full px-3 py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="owner">👑 Owner - Full Access</option>
                  <option value="admin">🔧 Admin - Full Access</option>
                  <option value="finance">💰 Finance - Pricing & Costing</option>
                  <option value="ops">📦 Ops - Operations & Planning</option>
                  <option value="sales">🛒 Sales - Sales & Pricing</option>
                  <option value="pricing">💵 Pricing - Pricing Focus</option>
                  <option value="analyst">📊 Analyst - Read-Only</option>
                  <option value="viewer">👁️ Viewer - Basic View</option>
                </select>
              </div>
              
              <div className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg">
                <div className="text-sm text-blue-900 dark:text-blue-200">
                  <p className="font-medium mb-1">Current Role: <span className="capitalize">{userRoles[0]}</span></p>
                  <p className="text-xs text-blue-700 dark:text-blue-300">
                    Columns are filtered based on your role permissions. Change role to see different views.
                  </p>
                </div>
              </div>

              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Grid Info</span>
                </div>
                <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                  <div className="flex justify-between">
                    <span>Total Products:</span>
                    <Badge variant="secondary" className="bg-gray-100 dark:bg-gray-700">{onGetProductCount?.() || 0}</Badge>
                  </div>
                  <div className="flex justify-between">
                    <span>Available Columns:</span>
                    <Badge variant="secondary" className="bg-gray-100 dark:bg-gray-700">{availableColumns.length}</Badge>
                  </div>
                  <div className="flex justify-between">
                    <span>Selected Columns:</span>
                    <Badge variant="secondary" className="bg-gray-100 dark:bg-gray-700">{selectedColumns.length}</Badge>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>
        </Tabs>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
          <div className="flex items-center gap-2">
            <Input
              placeholder="Variant name..."
              value={variantName}
              onChange={(e) => setVariantName(e.target.value)}
              className="w-48 text-gray-900 dark:text-white bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 placeholder:text-gray-400 dark:placeholder:text-gray-500"
            />
          </div>
          
          <div className="flex items-center gap-2">
            <Button 
              variant="ghost" 
              onClick={handleReset}
              className="text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
            >
              <RotateCcw className="w-4 h-4 mr-1" />
              Reset
            </Button>
            <Button 
              variant="outline" 
              onClick={handleSaveVariant} 
              disabled={!variantName.trim()}
              className="bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:text-gray-400 dark:disabled:text-gray-600"
            >
              <Save className="w-4 h-4 mr-1" />
              Save as Variant
            </Button>
            <Button 
              onClick={handleApply}
              className="bg-blue-600 dark:bg-blue-500 hover:bg-blue-700 dark:hover:bg-blue-600 text-white"
            >
              Apply
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.getElementById('portal-root') || document.body
  );
};

export default ViewSettingsModal;